/**
 * useBackendBranding · v2.7.4
 * ───────────────────────────
 * Public consumer hook for the Backend Branding engine.
 *
 * Fetches GET /api/site/backend-branding (no auth needed — public endpoint),
 * resolves the (image > color > default) priority server-side, and returns
 * ready-to-spread inline style objects for the page background + nav bar.
 *
 * Usage:
 *   const { backgroundStyle, navStyle } = useBackendBranding();
 *   <div style={backgroundStyle}> ... <header style={navStyle}>
 *
 * Polls every 30s so admins see their saved branding reflect within
 * 30 seconds across all open tabs without a manual refresh.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const REFRESH_MS = 30_000;

function toAssetURL(maybeRelative) {
  if (!maybeRelative) return "";
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  return `${BASE}${maybeRelative}`;
}

function elementStyle(el) {
  if (!el) return {};
  if (el.kind === "image" && el.value) {
    return {
      backgroundImage: `url("${toAssetURL(el.value)}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    };
  }
  if (el.kind === "color" && el.value) {
    return { backgroundColor: el.value };
  }
  return {};
}

export default function useBackendBranding() {
  const [elements, setElements] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchOnce = async () => {
      try {
        const { data } = await axios.get(`${BASE}/api/site/backend-branding`, {
          timeout: 8000,
        });
        if (mounted) setElements(data?.elements || null);
      } catch {
        // Silent — falls back to whatever theme is already on the page.
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, REFRESH_MS);
    // Refetch instantly when the editor pushes a change.
    window.addEventListener("backend-branding-changed", fetchOnce);
    return () => {
      mounted = false;
      clearInterval(id);
      window.removeEventListener("backend-branding-changed", fetchOnce);
    };
  }, []);

  const backgroundStyle = useMemo(
    () => elementStyle(elements?.global_background),
    [elements]
  );
  const navStyle = useMemo(
    () => elementStyle(elements?.navigation_bar),
    [elements]
  );

  return { elements, backgroundStyle, navStyle };
}
